"use client";

import { ColumnDef } from "@tanstack/react-table";
import { capitalize, upperCase } from "lodash";
import { Ban, CircleArrowDown, CircleArrowUp, X } from "lucide-react";
import moment from "moment";
import Image from "next/image";

import ExchangeImage from "@/components/global/exchange-swticher/exchangeIcon";
import { DataTableColumnHeader } from "@/components/ui/data-table-col-header";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { formatNumber } from "@/lib/utils";
import * as m from "@/paraglide/messages";

import CancelModal from "./cancel";
import { OpenOrderType } from "./types";
import { orderTypesEnums } from "../types";

export const columns: ColumnDef<OpenOrderType>[] = [
  {
    accessorKey: "order_data",
    meta: {
      title: m.type(),
    },
    header: ({ column }) => {
      return (
        <DataTableColumnHeader
          className="justify-start"
          column={column}
          title={m.type()}
        />
      );
    },
    cell: ({ row }) => {
      return (
        <div className="flex flex-col gap-1">
          <div className="flex flex-1 gap-2 items-center font-semibold text-base">
            {row.original.order_data.side === "BUY" ? (
              <CircleArrowUp className="rotate-45 text-success dark:text-green-600" />
            ) : (
              <CircleArrowDown className="-rotate-45 text-destructive dark:text-red-600" />
            )}
            {(row.original.order_data.side === "BUY" ? m.buy() : m.sell()) ??
              m.sell()}
          </div>
          <p className="text-left text-xs font-bold text-muted-foreground">
            {row.original.type === "ENTRY" &&
              capitalize(row.original.order_data.entry_type) + "-"}
            {row.original.type === "T_SL"
              ? row.original.order_data.trailing_delta
                ? "Percentage"
                : "Breakeven"
              : orderTypesEnums[
                  row.original.type as keyof typeof orderTypesEnums
                ]}
          </p>
        </div>
      );
    },
  },
  {
    accessorKey: "created_at",
    meta: {
      title: m.date(),
    },
    header: ({ column }) => {
      return (
        <DataTableColumnHeader
          className="justify-start"
          column={column}
          title={m.date()}
        />
      );
    },
    cell: ({ row }) => {
      return (
        <div className="font-medium text-sm flex flex-col gap-1">
          {moment(row.original.created_at).format("MMMM DD,YYYY")}
          <p className="text-xs">
            {moment(row.original.created_at).format("HH:mm:ss")}
          </p>
        </div>
      );
    },
  },
  {
    accessorKey: "quantity",
    meta: {
      title: m.quantity(),
    },
    header: ({ column }) => {
      return (
        <DataTableColumnHeader
          className="justify-start"
          column={column}
          title={m.quantity()}
        />
      );
    },
    cell: ({ row }) => {
      return (
        <div className="font-medium">
          {formatNumber(row.original.quantity)}{" "}
          {row.original.asset_name.toUpperCase()}
        </div>
      );
    },
  },
  {
    accessorKey: "value",
    meta: {
      title: m.price(),
    },
    header: ({ column }) => {
      return (
        <DataTableColumnHeader
          className="justify-start"
          column={column}
          title={m.price()}
        />
      );
    },
    cell: ({ row }) => {
      const actPrice = row.original.order_data?.activation_price;
      const trailingPerc = row.original?.order_data?.trailing_delta;
      return (
        <div className="font-medium min-w-fit">
          <div className="w-full">
            <Tooltip delayDuration={0}>
              <TooltipTrigger disabled={!actPrice}>
                {trailingPerc
                  ? `${(trailingPerc as any) * 100}%`
                  : `${formatNumber(row.original.value, true)}`}
              </TooltipTrigger>

              {!actPrice ? (
                <></>
              ) : (
                <TooltipContent side="left" className="flex items-center gap-1">
                  <p>{m.activation_price()}:</p>
                  <p className="text-xs">{formatNumber(actPrice ?? 0, true)}</p>
                </TooltipContent>
              )}
            </Tooltip>
          </div>
        </div>
      );
    },
    size: 80,
  },
  {
    accessorKey: "order_status",
    meta: {
      title: m.status(),
    },
    header: ({ column }) => {
      return (
        <DataTableColumnHeader
          className="justify-start"
          column={column}
          title={m.status()}
        />
      );
    },
    cell: ({ row }) => {
      return (
        <div className="font-semibold">
          {row.original.entry_order_id == null ? (
            capitalize(upperCase(row.original.order_status.split("_")[0]))
          ) : (
            <Tooltip delayDuration={0}>
              <TooltipTrigger>
                <p>{capitalize(upperCase(row.original.order_status))}</p>
              </TooltipTrigger>

              <TooltipContent side="top" className="flex items-center gap-4">
                <div className="grid gap-2">
                  <p>{m.helpful_still_poodle_win()}.</p>
                </div>
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      );
    },
  },
  {
    accessorKey: "order_provider",
    meta: {
      title: m.exchanges(),
    },
    header: ({ column }) => {
      return (
        <DataTableColumnHeader
          className="justify-start"
          column={column}
          title={m.exchanges()}
          enableSorting={false}
        />
      );
    },
    cell: ({ row }) => {
      return (
        <div className="ps-8">
          {row.original.order_data.notes === "paper" ? (
            <Image
              key={row.index}
              src="/paper.svg"
              alt="Paper Account Icon"
              width={20}
              height={20}
            />
          ) : (
            <ExchangeImage
              providerId={row.original.order_provider}
              height={20}
              width={20}
            />
          )}
        </div>
      );
    },
  },
  {
    accessorKey: "Actions",
    header: ({ column }) => {
      return (
        <DataTableColumnHeader
          className="justify-start"
          column={column}
          title={m.action()}
          enableSorting={false}
        />
      );
    },
    cell: ({ row }) => {
      return (
        <div>
          {row.original.type === "ENTRY" &&
          row.original.order_data.entry_type === "MARKET" ? (
            <Tooltip delayDuration={0}>
              <TooltipTrigger className="ps-4">
                <Ban className="w-5 text-orange-500" />
              </TooltipTrigger>

              <TooltipContent side="top" className="flex items-center gap-4">
                <div className="grid gap-2">
                  <p>{m.flat_bright_hyena_list()}</p>
                </div>
              </TooltipContent>
            </Tooltip>
          ) : row?.original?.order_status?.toLowerCase() === "cancelled" ? (
            <Tooltip delayDuration={0}>
              <TooltipTrigger className="ps-4">
                <X className="w-5 text-red-500" />
              </TooltipTrigger>

              <TooltipContent side="top" className="flex items-center gap-4">
                <div className="grid gap-2">
                  <p>{m.already_cancellled()}</p>
                </div>
              </TooltipContent>
            </Tooltip>
          ) : (
            <CancelModal orderId={row.original.id} />
          )}
        </div>
      );
    },
    enableHiding: false,
  },
];

export const mobileColumns: ColumnDef<OpenOrderType>[] = [
  {
    accessorKey: "order_data",
    meta: {
      title: m.type(),
    },
    header: ({ column }) => {
      return (
        <DataTableColumnHeader
          className="justify-start"
          column={column}
          title={m.type()}
        />
      );
    },
    cell: ({ row }) => {
      return (
        <div className="flex flex-col gap-1">
          <div className="flex flex-1 gap-2 items-center font-semibold text-base">
            {row.original.order_data.notes === "paper" ? (
              <Image
                key={row.index}
                src="/paper.svg"
                alt="Paper Account Icon"
                width={20}
                height={20}
              />
            ) : (
              <ExchangeImage
                providerId={row.original.order_provider}
                height={20}
                width={20}
              />
            )}
            {row.original.order_data.side === "BUY"
              ? m.buy()
              : m.sell() ?? m.sell()}
          </div>
          <p className="text-left text-xs font-bold text-muted-foreground">
            {row.original.type === "ENTRY" &&
              capitalize(row.original.order_data.entry_type) + "-"}
            {row.original.type === "T_SL"
              ? row.original.order_data.trailing_delta
                ? "Percentage"
                : "Breakeven"
              : orderTypesEnums[
                  row.original.type as keyof typeof orderTypesEnums
                ]}
          </p>
        </div>
      );
    },
  },
  {
    accessorKey: "created_at",
    meta: {
      title: m.date(),
    },
    header: ({ column }) => {
      return (
        <DataTableColumnHeader
          className="justify-start"
          column={column}
          title={m.date()}
        />
      );
    },
    cell: ({ row }) => {
      return (
        <div className="font-medium text-sm flex flex-col gap-1">
          {moment(row.original.created_at).format("MM DD,YYYY")}
          <p className="text-xs">
            {moment(row.original.created_at).format("HH:mm:ss")}
          </p>
        </div>
      );
    },
  },
  {
    accessorKey: "quantity",
    meta: {
      title: m.quantity(),
    },
    header: ({ column }) => {
      return <DataTableColumnHeader column={column} title={m.quantity()} />;
    },
    cell: ({ row }) => {
      const actPrice = row.original.order_data?.activation_price;
      const trailingPerc = row.original?.order_data?.trailing_delta;
      return (
        <>
          <div className="w-full text-right">
            <Tooltip delayDuration={0}>
              <TooltipTrigger disabled={!actPrice}>
                <p className="font-medium text-left">
                  {formatNumber(row.original.quantity)}{" "}
                  {row.original.asset_name.toUpperCase()}
                </p>
              </TooltipTrigger>

              {!actPrice ? (
                <></>
              ) : (
                <TooltipContent side="left" className="flex items-center gap-1">
                  <p>{m.activation_price()}:</p>
                  <p className="text-xs">{formatNumber(actPrice ?? 0, true)}</p>
                </TooltipContent>
              )}
            </Tooltip>
          </div>
          <p className="text-right">
            {trailingPerc
              ? `${(trailingPerc as any) * 100}%`
              : `${formatNumber(row.original.value, true)}`}
          </p>
        </>
      );
    },
  },
  {
    accessorKey: "Actions",
    header: ({ column }) => {
      return (
        <DataTableColumnHeader
          className="justify-start"
          column={column}
          title=""
          enableSorting={false}
        />
      );
    },
    cell: ({ row }) => {
      return (
        <div>
          {row.original.type === "ENTRY" &&
          row.original.order_data.entry_type === "MARKET" ? (
            <Tooltip delayDuration={0}>
              <TooltipTrigger className="ps-4">
                <Ban className="w-5 text-orange-500" />
              </TooltipTrigger>

              <TooltipContent side="top" className="flex items-center gap-4">
                <div className="grid gap-2">
                  <p>{m.flat_bright_hyena_list()}</p>
                </div>
              </TooltipContent>
            </Tooltip>
          ) : row?.original?.order_status?.toLowerCase() === "cancelled" ? (
            <Tooltip delayDuration={0}>
              <TooltipTrigger className="ps-4">
                <X className="w-5 text-red-500" />
              </TooltipTrigger>

              <TooltipContent side="top" className="flex items-center gap-4">
                <div className="grid gap-2">
                  <p>{m.already_cancellled()}</p>
                </div>
              </TooltipContent>
            </Tooltip>
          ) : (
            <CancelModal orderId={row.original.id} />
          )}
        </div>
      );
    },
    enableHiding: false,
  },
];
